import React, { ChangeEvent, FormEvent, memo, useState } from "react";
import { FieldArray, FormikProps, useFormikContext } from "formik";
import { Data } from "../../../store/card/types";
import {
  Grid,
  Box,
  Button,
  Typography,
  FormControlLabel,
  Divider,
  TextField,
} from "@mui/material";
import pageStyles from "../../pageStyles";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import { Add, Delete, Payment } from "@mui/icons-material";
import Confirm from "../../../common/confirm/Confirm";
import CustomizedCheckbox from "../../../common/formElements/Checkbox";
import BinForm from "./BinForm";
import TextInput from "../../../common/formElements/TextInput";
import SelectDropDown from "../../../common/formElements/SelectDropDown";
import Autocomplete from "@mui/material/Autocomplete";
import { useDispatch } from "react-redux";
import { saveRequest } from "../../../store/card/actions";

type CardBinOption = {
  desc: string;
  index: number;
};

const CardBinForm = () => {
  const dispatch = useDispatch();
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;
  const styles = pageStyles();
  const [selectedCardBinIdx, setSelectedCardBinIdx] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  const cardBinList: CardBinOption[] =
    values.attributes.CardBin?.map((card, index) => ({
      desc: `${
        card.DescLong || card.DescShort
          ? `${card.DescLong} | ${card.DescShort}`
          : "No Name"
      }`,
      index,
    })) || [];

  const handleChangeCardBin = (
    e: ChangeEvent<{}>,
    value: CardBinOption | null
  ) => {
    setSelectedCardBinIdx(value?.index || 0);
  };

  const handleDeleteCardBin = (currentIndex: number) => {
    const currentCardBinArray = values.attributes.CardBin || [];
    const newCardBinArray = [...currentCardBinArray];
    newCardBinArray.splice(currentIndex, 1);
    setFieldValue("attributes.CardBin", newCardBinArray);

    const updatedValues = {
      ...values,
      attributes: {
        ...values.attributes,
        CardBin: newCardBinArray,
      },
    };
    dispatch(saveRequest(updatedValues));

    setShowConfirm(false);
  };

  return (
    <FieldArray
      name={`attributes.CardBin`}
      render={(arrayHelpers) => {
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SectionWrapper title="Select Card Bin" isChild isWhiteBG>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  width="100%"
                >
                  <Autocomplete
                    fullWidth
                    defaultValue={cardBinList[0]}
                    options={cardBinList}
                    getOptionLabel={(option) => option.desc}
                    onChange={handleChangeCardBin}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Card Bin"
                        variant="outlined"
                      />
                    )}
                    value={
                      cardBinList.find(
                        (card) => card.index === selectedCardBinIdx
                      ) || null
                    }
                  />
                  <Button
                    color="primary"
                    variant="contained"
                    size="medium"
                    endIcon={<Add />}
                    onClick={() => {
                      arrayHelpers.push({
                        DescLong: "",
                        DescShort: "",
                        DUKPT: false,
                        PINBlock: "",
                        Bin: [],
                      });

                      setSelectedCardBinIdx(
                        values.attributes.CardBin?.length || 0
                      );
                    }}
                    style={{ marginTop: "15px" }}
                  >
                    Create Card Bin
                  </Button>
                </Box>
              </SectionWrapper>
            </Grid>
            <Grid item xs={12} md={9}>
              <SectionWrapper title="Card Bin Details" isChild isWhiteBG>
                {values.attributes.CardBin?.length ? (
                  <>
                    {values.attributes.CardBin?.[selectedCardBinIdx] && (
                      <Box key={selectedCardBinIdx} width="100%">
                        <Box className={styles.processorTitleWrapper}>
                          <Typography className={styles.processorTitle}>
                            <Payment style={{ marginRight: "5px" }} />
                            {
                              values.attributes.CardBin?.[selectedCardBinIdx]
                                .DescLong
                            }
                          </Typography>
                          <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                              setShowConfirm(true);
                            }}
                            size="small"
                            startIcon={<Delete />}
                          >
                            Delete Card Bin
                          </Button>
                        </Box>

                        <Box marginTop={2}>
                          <Divider />
                        </Box>

                        <Grid container style={{ marginTop: "15px" }}>
                          <Grid item xs={12}>
                            <Box p={2}>
                              <Grid
                                container
                                alignItems="flex-start"
                                spacing={3}
                              >
                                <Grid item xs={6}>
                                  <TextInput
                                    fieldname={`attributes.CardBin[${selectedCardBinIdx}].DescLong`}
                                    label="Desc Long"
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextInput
                                    fieldname={`attributes.CardBin[${selectedCardBinIdx}].DescShort`}
                                    label="Desc Short"
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <FormControlLabel
                                    control={
                                      <CustomizedCheckbox
                                        checked={
                                          values.attributes.CardBin?.[
                                            selectedCardBinIdx
                                          ].DUKPT
                                        }
                                        onChange={(
                                          event: FormEvent<HTMLInputElement>
                                        ) => {
                                          setFieldValue(
                                            `attributes.CardBin[${selectedCardBinIdx}].DUKPT`,
                                            event.currentTarget.checked
                                          );
                                        }}
                                        name={`attributes.CardBin[${selectedCardBinIdx}].DUKPT`}
                                      />
                                    }
                                    label="DUKPT"
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <SelectDropDown
                                    label="PIN Block"
                                    fieldname={`attributes.CardBin[${selectedCardBinIdx}].PINBlock`}
                                    options={["Format 0", "Format 3"]}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                            <Box marginTop={2} marginBottom={3}>
                              <Divider />
                            </Box>
                            <BinForm
                              cardBin={
                                values.attributes.CardBin?.[selectedCardBinIdx]
                              }
                              cardBinIndex={selectedCardBinIdx}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Confirm
                      isOpen={showConfirm}
                      onCancel={() => setShowConfirm(false)}
                      onConfirm={() => handleDeleteCardBin(selectedCardBinIdx)}
                      title="Delete Card Bin"
                      icon="danger"
                      confirmLabel="Delete"
                      contentText={`You are about to delete a Card Bin`}
                    />
                  </>
                ) : (
                  <>{`You don't have any Card Bin yet. Please create one.`}</>
                )}
              </SectionWrapper>
            </Grid>
          </Grid>
        );
      }}
    />
  );
};

export default memo(CardBinForm);
